import React from "react"
import Link from "./LinkWrapper"
import { urlHelper } from "../util"
import get from "lodash/get"
import Button, { ButtonProps } from "./Button"
import Video from "./Video"
import ContentModal from "./ContentModal"
import { linkItemTransformer } from "../utils/transformers"
import { BodyStylesProps } from "./BodyStyles"
import { cva } from "class-variance-authority"

const linkClasses = cva(
  "font-normal transition-colors hover:underline focus:outline-none focus:ring",
  {
    variants: {
      contrast: {
        true: "text-white hover:text-gray-200",
        false: "text-action-link hover:text-action",
      },
    },
    defaultVariants: {
      contrast: false,
    },
  }
)

export interface LinkElementProps {
  /**
   * The `Link` item from Kontent to render. Determines the link’s label, destination,
   * variant (style), icon, and may override the contrast prop when the variant is a button.
   */
  contentItem: any
  /**
   * The theme of the link. Defaults to `default`
   */
  linkTheme?: ButtonProps["buttonTheme"]
  /**
   * **Deprecated** Does nothing
   * @deprecated Use `linkTheme` instead
   */
  theme?: string
  /**
   * Whether to use the contrast color scheme. Should be used for links on dark
   * backgrounds. May be overridden when displaying as a button by data from Kontent.
   */
  contrast?: boolean
  /**
   * The current page’s colorScheme. Passed to modal component so content
   * matches page.
   */
  colorScheme?: BodyStylesProps["colorScheme"]
}

/**
 * Handles "Link" content items from Kontent and renders them as a link or
 * button. Can be used for standard links or links to open content or video
 * in a modal.
 */
const LinkElement = ({
  contentItem,
  linkTheme,
  theme,
  contrast,
  colorScheme,
}: LinkElementProps) => {
  const buttonColorScheme =
    get(contentItem, "elements.color_scheme.value[0].codename") || "inherit"
  const label = get(contentItem, "elements.label.value")
  const style = get(contentItem, "elements.style.value[0].codename")
  const icon = get(contentItem, "elements.icon.value[0].codename")
  const linkTo = get(contentItem, "elements.link_to.value[0]")
  const linkToType = get(linkTo, "system.type")

  /* Inherits the contrast value passed into the component unless `buttonColorScheme` is set to `contrast` or `normal`
   * in Kontent. If `contrast` or `normal` are set, they override the `contrast` prop passed into the component.
   */
  const _contrast = {
    inherit: contrast,
    normal: false,
    contrast: true,
  }[buttonColorScheme || "inherit"]

  const hasIcon = icon && icon !== "none"
  let iconName = hasIcon && icon.replace(/_/g, "-")

  /**
   * Handle video and modal links before other links so link styles for videos
   * and modals can be either buttons or normal links
   */
  if (linkToType === `video`) {
    return (
      <Video
        contrast={contrast}
        colorScheme={`default`}
        format={style === `button` ? `button` : `link`}
        linkLabel={label}
        playInModal={true}
        video={linkTo}
      />
    )
  }

  // TODO: Handle style selection in content modal component to support link
  // and button styles
  if (linkToType === `modal`) {
    return (
      <ContentModal
        contentItem={linkTo}
        colorScheme={colorScheme}
        link={{
          ...linkItemTransformer(contentItem),
          ...{ iconColorScheme: "default" },
        }}
      />
    )
  }

  /*
   * All other links will be either a button or normal link based on the
   * style prop
   */
  if (style === `button`) {
    return (
      <Button
        buttonType="pill"
        buttonTheme={linkTheme}
        contrast={_contrast}
        as={Link}
        to={urlHelper(linkTo)}
        hasIcon={hasIcon}
        data-analytics-title={get(linkTo, "system.name")}
      >
        {hasIcon && <i className={`${iconName} mr-1`} />}
        {label}
      </Button>
    )
  } else {
    return (
      <Link
        to={urlHelper(linkTo)}
        className={linkClasses({ contrast })}
        data-analytics-title={get(linkTo, "system.name")}
      >
        {label}
        {style === "more" && (
          <i
            className="icon-chevron-right"
            style={{ width: `1em`, marginRight: `-1em` }}
          ></i>
        )}
      </Link>
    )
  }
}

export default LinkElement
